import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
   Modal,
   ModalOverlay,
   ModalContent,
   ModalHeader,
   ModalBody,
   ModalCloseButton,
   Box,
   Text,
   Table,
   Thead,
   Tbody,
   Tr,
   Th,
   Td,
   Badge,
   Code,
   VStack,
   HStack,
   Alert,
   AlertIcon,
   Button,
   useDisclosure,
   Grid,
   GridItem,
   Link,
   Icon,
   useToast,
   Spinner,
   FormControl,
   FormLabel,
   Input,
   ModalFooter,
} from '@chakra-ui/react';
import { InfoIcon, ExternalLinkIcon, RepeatIcon } from '@chakra-ui/icons';
import { addVoucher } from '../../../../redux/actions/voucher';
import { addPurchaseInvoice } from '../../../../redux/actions/purchaseInvoice';
import { addPurchaseReturnInvoice } from '../../../../redux/actions/purchaseReturnInvoice';
import { addSaleInvoice } from '../../../../redux/actions/salesInvoice';
import { addSaleReturnInvoice } from '../../../../redux/actions/salesReturnInvoice';
import { addAccount } from '../../../../redux/actions/account';
import { addItem } from '../../../../redux/actions/item';
import { addDaybook } from '../../../../redux/actions/daybook';
import { addBillTerm } from '../../../../redux/actions/billTerm';
import { addBank } from '../../../../redux/actions/bank';
import { addTransport } from '../../../../redux/actions/transport';
import { addDriver } from '../../../../redux/actions/driver';
import { addVehicle } from '../../../../redux/actions/vehicle';
import { addColor } from '../../../../redux/actions/color';
import { addStyle } from '../../../../redux/actions/style';
import { addSize } from '../../../../redux/actions/size';
import { addHsn } from '../../../../redux/actions/hsn';
import { addArea } from '../../../../redux/actions/area';
import { addSeries } from '../../../../redux/actions/series';
import { addBrand } from '../../../../redux/actions/brand';
import { getSingleLog, updateLogs } from '../../../../redux/actions/logs';
import { FaLock } from 'react-icons/fa6';
import { CopyIcon } from '@chakra-ui/icons';
import moment from 'moment';
import { useSelector, useDispatch } from 'react-redux';
import { useUser } from 'contexts';
import { checkDocNo as salesCheckDocNo } from '../../../../redux/actions/salesInvoice';
import { checkDocNo as purchaseCheckDocNo } from '../../../../redux/actions/purchaseInvoice';
import { checkDocNo as salesReturnCheckDocNo } from '../../../../redux/actions/salesReturnInvoice';
import { checkDocNo as purchaseReturnCheckDocNo } from '../../../../redux/actions/purchaseReturnInvoice';
import { checkDocNo as issueCheckDocNo, addIssueInventory } from '../../../../redux/actions/issueInventory';
import { checkDocNo as receiveCheckDocNo, addReceiveInventory } from '../../../../redux/actions/receiveInventory';
import Swal from 'sweetalert2';
import { checkLastDocNumberVoucher } from '../../../../redux/actions/voucher';
import {
   ACCOUNT_FORM_FIELDS,
   DAYBOOK_FORM_FIELDS,
   ITEM_FORM_FIELDS,
   BANK_BRANCH_FORM_FIELDS,
   BILLTERM_FORM_FIELDS,
   TRANSPORT_FORM_FIELDS,
   DRIVER_FORM_FIELDS,
   VEHICLE_FORM_FIELDS,
   COLOR_FORM_FIELDS,
   STYLE_FORM_FIELDS,
   SIZE_FORM_FIELDS,
   AREA_FORM_FIELDS,
   BRAND_FORM_FIELDS,
} from '../../../../constants/formFields';
import { safeDate } from '../../../../utils/util';

// Constants for performance
const EMPTY_ARRAY = [];
const EMPTY_OBJECT = {};
const ID_FIELDS_TO_EXCLUDE = new Set([
   'id',
   '_id',
   'ID',
   '_ID',
   'userId',
   'productId',
   'orderId',
   'UserId',
   'ProductId',
   'OrderId',
   'companyId',
   'accountId',
   'brokerId',
   'daybookId',
   'warehouseId',
   'representativeId',
   'voucherId',
   'itemId',
   'hsnId',
   'sacId',
   'subHSNId',
   'billtoStateId',
   'shiptoStateId',
   'qUnitId1',
   'qUnitId2',
   'qUnitId3',
   'issueLineId',
   'pinvLineDetailId',
   'outAdjustmentId',
   'updatedU',
]);

// Pre-computed field name mappings
const FIELD_NAME_MAPPINGS = {
   daybookId: 'Daybook',
   docno: 'Document Number',
   transactionAt: 'Transaction Date',
   companyId: 'Company',
   accountId: 'Account',
   brokerId: 'Broker',
   basicAmount: 'Basic Amount',
   additionAmount: 'Addition Amount',
   deductionAmount: 'Deduction Amount',
   netAmount: 'Net Amount',
   dueDays: 'Due Days',
   dueAt: 'Due Date',
   billtoStateId: 'Bill To State',
   shiptoStateId: 'Ship To State',
   warehouseId: 'Warehouse',
   partyNo: 'Party Number',
   partyDate: 'Party Date',
   notes: 'Notes',
   representativeId: 'Representative',
   voucherId: 'Voucher',
   gstAmount: 'GST Amount',
   taxableAmount: 'Taxable Amount',
   gstTxnType: 'GST Transaction Type',
   gstSubTxnType: 'GST Sub Transaction Type',
   assesseType: 'Assesse Type',
   distance: 'Distance',
   originalBillDate: 'Original Bill Date',
   originalBillNo: 'Original Bill Number',
   issueType: 'Issue Type',
   requestedBy: 'Requested By',
   receiptType: 'Receipt Type',
   receivedFrom: 'Received From',
   hsnId: 'HSN Code',
   sacId: 'SAC Code',
   itemId: 'Item',
   itemRate: 'Rate',
   amount: 'Amount',
   netAmount: 'Net Amount',
   qty1: 'Quantity 1',
   qty2: 'Quantity 2',
   qty3: 'Quantity 3',
   taxableAmount: 'Taxable Amount',
   gstPercent: 'GST %',
   cgst: 'CGST',
   sgst: 'SGST',
   igst: 'IGST',
   typeOfSupply: 'Supply Type',
   ratePer: 'Rate Per',
   uomNoForRate: 'UOM',
   qUnitId1: 'Unit 1',
   qUnitId2: 'Unit 2',
   qUnitId3: 'Unit 3',
   addLess1Percent: 'Add/Less 1 %',
   addLess1Amount: 'Add/Less 1 Amount',
   addLess2Percent: 'Add/Less 2 %',
   addLess2Amount: 'Add/Less 2 Amount',
   addLess3Percent: 'Add/Less 3 %',
   addLess3Amount: 'Add/Less 3 Amount',
   netGst: 'Net GST',
   subHSNId: 'Sub HSN',
};

const DEVICE_DISPLAY_NAMES = {
   url: 'Request URL',
   method: 'HTTP Method',
   publicIp: 'Public IP Address',
   userAgent: 'User Agent',
   currentUrl: 'Current Page URL',
   deviceOsName: 'Operating System',
   deviceBrowser: 'Browser',
   browserPlugins: 'Browser Plugins',
   deviceLanguage: 'Language',
   devicePlatform: 'Platform',
   deviceTimezone: 'Timezone',
   deviceUserAgent: 'Device User Agent',
   devicePixelRatio: 'Pixel Ratio',
   requestTimestamp: 'Request Time',
   storageIndexedDb: 'IndexedDB Support',
   deviceScreenWidth: 'Screen Width',
   deviceOnlineStatus: 'Online Status',
   deviceScreenHeight: 'Screen Height',
   deviceCookieEnabled: 'Cookies Enabled',
   storageLocalStorage: 'Local Storage',
   deviceMaxTouchPoints: 'Max Touch Points',
   storageSessionStorage: 'Session Storage',
   deviceHardwareConcurrency: 'CPU Cores',
   latitude: 'Latitude',
   longitude: 'Longitude',
};

// Memoized components
const JsonViewer = memo(({ payload }) => {
   const toast = useToast();

   const handleCopy = useCallback(() => {
      navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      toast({
         title: 'Copied!',
         description: 'Full JSON copied to clipboard',
         status: 'success',
         duration: 2000,
         isClosable: true,
      });
   }, [payload, toast]);

   return (
      <Box border="1px solid" borderColor="gray.300" borderRadius="md" p={4} bg="gray.50">
         <Box display="flex" justifyContent="flex-end" mb={2}>
            <Button size="sm" leftIcon={<CopyIcon />} onClick={handleCopy}>
               Copy JSON
            </Button>
         </Box>
         <Code display="block" whiteSpace="pre" overflowX="auto" fontSize="sm" p={2} w="100%">
            {JSON.stringify(payload, null, 2)}
         </Code>
      </Box>
   );
});

const DifferenceRow = memo(({ diff }) => {
   const formatValue = useCallback((value) => {
      if (value === null) return 'null';
      if (value === undefined) return 'undefined';
      if (typeof value === 'object') return JSON.stringify(value);
      return String(value);
   }, []);

   const getChangeTypeColor = useCallback((type) => {
      switch (type) {
         case 'added':
            return 'green';
         case 'removed':
            return 'red';
         case 'modified':
            return 'blue';
         default:
            return 'gray';
      }
   }, []);

   return (
      <Tr _hover={{ bg: 'gray.50' }}>
         <Td border="1px solid" borderColor="black">
            <Code fontSize="sm" fontFamily="mono">
               {diff.field}
            </Code>
         </Td>
         <Td border="1px solid" borderColor="black">
            <Code
               bg={diff.type === 'removed' ? 'red.50' : 'gray.100'}
               p={2}
               borderRadius="sm"
               fontSize="xs"
               color={diff.type === 'removed' ? 'red.600' : 'inherit'}
               display="block"
               whiteSpace="pre-wrap"
               wordBreak="break-all">
               {formatValue(diff.oldValue)}
            </Code>
         </Td>
         <Td border="1px solid" borderColor="black">
            <Code
               bg={diff.type === 'added' ? 'green.50' : 'gray.100'}
               p={2}
               borderRadius="sm"
               fontSize="xs"
               color={diff.type === 'added' ? 'green.600' : 'inherit'}
               display="block"
               whiteSpace="pre-wrap"
               wordBreak="break-all">
               {formatValue(diff.newValue)}
            </Code>
         </Td>
         <Td border="1px solid" borderColor="black">
            <Badge colorScheme={getChangeTypeColor(diff.type)} variant="subtle" fontSize="xs">
               {diff.type}
            </Badge>
         </Td>
      </Tr>
   );
});

const DeviceInfoItem = memo(({ key: itemKey, value }) => {
   const formatDeviceValue = useCallback((val) => {
      if (val === null || val === undefined) return 'N/A';
      if (typeof val === 'string' && val.startsWith('[') && val.endsWith(']')) {
         try {
            const parsed = JSON.parse(val);
            return JSON.stringify(parsed, null, 2);
         } catch (e) {
            return val;
         }
      }
      return String(val);
   }, []);

   const colSpan = ['browserPlugins', 'userAgent', 'deviceUserAgent'].includes(itemKey) ? 2 : 1;

   return (
      <GridItem colSpan={colSpan}>
         <Box p={3} borderWidth={1} borderRadius="md" bg="gray.50">
            <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={1}>
               {DEVICE_DISPLAY_NAMES[itemKey] || itemKey}
            </Text>
            <Code
               p={2}
               borderRadius="sm"
               fontSize="xs"
               display="block"
               whiteSpace="pre-wrap"
               wordBreak="break-all"
               bg="white">
               {formatDeviceValue(value)}
            </Code>
         </Box>
      </GridItem>
   );
});

// Main component
const JsonDifferenceModal = ({ logData: initialLogData, isOpen, onClose }) => {
   const { user } = useUser();
   const [logData, setLogData] = useState(initialLogData);
   const [isLoadingLogData, setIsLoadingLogData] = useState(false);
   const [tempAccessCode, setTempAccessCode] = useState('');
   const [accessCode, setAccessCode] = useState('');
   const [isRestoring, setIsRestoring] = useState(false);
   const { isOpen: isDeviceModalOpen, onOpen: onDeviceModalOpen, onClose: onDeviceModalClose } = useDisclosure();
   const {
      isOpen: isAccessCodeModalOpen,
      onOpen: onAccessCodeModalOpen,
      onClose: onAccessCodeModalClose,
   } = useDisclosure();
   const { isOpen: isOpenPayload, onOpen: onOpenPayload, onClose: onClosePayload } = useDisclosure();

   const dispatch = useDispatch();
   const toast = useToast();
   const financialYears = useSelector((state) => state?.dashboard?.financialYears);

   // Refs for stable references
   const toastRef = useRef(toast);
   const dispatchRef = useRef(dispatch);
   const isOpenRef = useRef(isOpen);
   const initialLogDataRef = useRef(initialLogData);
   const accessCodeRef = useRef(accessCode);

   useEffect(() => {
      toastRef.current = toast;
      dispatchRef.current = dispatch;
      isOpenRef.current = isOpen;
      initialLogDataRef.current = initialLogData;
      accessCodeRef.current = accessCode;
   }, [toast, dispatch, isOpen, initialLogData, accessCode]);

   // Optimized checkTransactionDocNo
   const checkTransactionDocNo = useCallback(
      async (transactionType, daybookId, docno, companyId) => {
         const financialYear = financialYears?.find((a) => a.financial_year === user?.financialYear);
         if (!financialYear) return null;

         const data = {
            daybookId,
            docno,
            companyId,
            fromDate: moment(financialYear?.fy_start_date, 'DD/MM/YYYY').format('YYYY-MM-DD'),
            toDate: moment(financialYear?.fy_end_date, 'DD/MM/YYYY').format('YYYY-MM-DD'),
         };

         const actionMap = {
            sales: salesCheckDocNo,
            purchases: purchaseCheckDocNo,
            purchasereturn: purchaseReturnCheckDocNo,
            salesreturn: salesReturnCheckDocNo,
            issue: issueCheckDocNo,
            receive: receiveCheckDocNo,
         };

         const action = actionMap[transactionType];
         if (action) {
            return await dispatchRef.current(action(data));
         }
         return null;
      },
      [financialYears, user?.financialYear],
   );

   // Helper functions moved outside component for better performance
   const getTypeSpecificHeader = (transactionType, newJson) => {
      const type = transactionType.toLowerCase();
      switch (type) {
         case 'purchases':
         case 'purchasereturn':
            return {};

         case 'sales':
         case 'salesreturn':
            return {};

         case 'issue':
            return {
               issueType: newJson.issueType || null,
               requestedBy: newJson.requestedBy || null,
            };

         case 'receive':
            return {
               receiptType: newJson.receiptType || null,
               receivedFrom: newJson.receivedFrom || null,
            };

         default:
            return {};
      }
   };

   const getTypeSpecificLineItem = (transactionType, item) => {
      const type = transactionType.toLowerCase();
      switch (type) {
         case 'purchases':
            return {
               stock: (item.purchaseLineDetail || []).map((stock) => ({
                  serialNo: stock.serialNo || '',
                  serialNo2: stock.serialNo2 || '',
                  barcode: stock.barcode || '',
                  note1: '',
                  isOld: stock.isOld || false,
                  itemId: stock.itemId || null,
                  itemName: stock.itemName || '',
                  warehouseId: item.warehouseId || null,
                  issueLineDetailId: item.issueLineDetailId || null,
               })),
            };
         case 'purchasereturn':
            return {
               stock: (item.purchaseLineDetail || item.purchaseReturnLineDetail || []).map((stock) => ({
                  serialNo: stock.serialNo || '',
                  serialNo2: stock.serialNo2 || '',
                  barcode: stock.barcode || '',
                  note1: '',
                  isOld: stock.isOld || false,
                  itemId: stock.itemId || null,
                  itemName: stock.itemName || '',
                  warehouseId: item.warehouseId || null,
                  salesReturnLineDetailId: stock.salesReturnLineDetailId || null,
                  purchaseLineDetailId: stock.purchaseLineDetailId || null,
               })),
            };

         case 'sales':
            return {
               stock: (item.salesLineDetail || []).map((stock) => ({
                  itemId: stock.itemId || null,
                  itemName: stock.itemName || '',
                  pcs: stock.pcs || stock.quantity1 || 1,
                  serialNo: stock.serialNo || '',
                  serialNo2: stock.serialNo2 || '',
                  barcode: stock.barcode || '',
                  isOld: stock.isOld || false,
                  pinvLineDetailId: stock.pinvLineDetailId || null,
                  salesReturnLineDetailId: stock.salesReturnLineDetailId || null,
                  warehouseId: stock.warehouseId || null,
               })),
            };
         case 'salesreturn':
            return {
               stock: (item.salesReturnLineDetail || item.salesLineDetail || []).map((stock) => ({
                  itemId: stock.itemId || null,
                  itemName: stock.itemName || '',
                  pcs: stock.pcs || stock.quantity1 || 1,
                  serialNo: stock.serialNo || '',
                  serialNo2: stock.serialNo2 || '',
                  barcode: stock.barcode || '',
                  isOld: stock.isOld || false,
                  salesLineDetailId: stock.salesLineDetailId || null,
                  warehouseId: stock.warehouseId || null,
               })),
               subHSNId: item.subHSNId || null,
            };

         case 'issue':
            return {
               stock: (item.issueLineDetail || []).map((stock) => ({
                  serialNo: stock.serialNo || '',
                  serialNo2: stock.serialNo2 || '',
                  barcode: stock.barcode || '',
                  note1: '',
                  isOld: stock.isOld || false,
                  itemId: stock.itemId || null,
                  itemName: stock.itemName || '',
                  warehouseId: item.warehouseId || null,
                  pinvLineDetailId: item.pinvLineDetailId || null,
                  receiveLineDetailId: item.receiveLineDetailId || null,
               })),
            };

         case 'receive':
            return {
               stock: (item.receiptLineDetail || []).map((stock) => ({
                  serialNo: stock.serialNo || '',
                  serialNo2: stock.serialNo2 || '',
                  barcode: stock.barcode || '',
                  note1: '',
                  isOld: stock.isOld || false,
                  itemId: stock.itemId || null,
                  itemName: stock.itemName || '',
                  warehouseId: item.warehouseId || null,
               })),
            };

         default:
            return {};
      }
   };

   const getBillTerms = (transactionType, newJson) => {
      const type = transactionType.toLowerCase();
      switch (type) {
         case 'purchases':
         case 'purchasereturn':
         case 'sales':
         case 'salesreturn':
            return []; // Return empty array for these types
         case 'issue':
         case 'receive':
            return [];
         default:
            return [];
      }
   };

   const getBillTermsKey = (transactionType) => {
      const type = transactionType.toLowerCase();
      switch (type) {
         case 'purchases':
         case 'purchasereturn':
            return 'purchaseBillTerms';
         case 'sales':
         case 'salesreturn':
            return 'saleBillTerms';
         case 'issue':
            return 'issueBillTerms';
         case 'receive':
            return 'billTerm';
         default:
            return 'billTerms';
      }
   };

   // Pre-defined action maps for faster lookups
   const TRANSACTION_ACTION_MAP = {
      purchases: addPurchaseInvoice,
      purchasereturn: addPurchaseReturnInvoice,
      sales: addSaleInvoice,
      salesreturn: addSaleReturnInvoice,
      issue: addIssueInventory,
      receive: addReceiveInventory,
   };

   const API_ACTION_MAP = {
      voucher: addVoucher,
      account: addAccount,
      item: addItem,
      daybook: addDaybook,
      billterm: addBillTerm,
      bankbranch: addBank,
      transport: addTransport,
      driver: addDriver,
      vehicle: addVehicle,
      color: addColor,
      style: addStyle,
      size: addSize,
      area: addArea,
      series: addSeries,
      brand: addBrand,
      hsnmain: addHsn,
   };

   // Optimized transformTransactionData
   const transformTransactionData = useCallback(async (newJson, transactionType) => {
      const type = transactionType.toLowerCase();

      // Base header with safe defaults
      const baseHeader = {
         daybookId: newJson.daybookId || null,
         docno: newJson.docno || null,
         transactionAt: safeDate(newJson.transactionAt),
         companyId: newJson.companyId || null,
         accountId: newJson.accountId || null,
         brokerId: newJson.brokerId || null,
         basicAmount: newJson.basicAmount || 0,
         additionAmount: newJson.additionAmount || 0,
         deductionAmount: newJson.deductionAmount || 0,
         netAmount: newJson.netAmount || 0,
         dueDays: newJson.dueDays || 0,
         dueAt: safeDate(newJson.dueAt),
         billtoStateId: newJson.billtoStateId || null,
         shiptoStateId: newJson.shiptoStateId || null,
         warehouseId: newJson.warehouseId || null,
         notes: newJson.notes || null,
         representativeId: newJson.representativeId || null,
         voucherId: null,
         gstAmount: newJson.gstAmount || 0,
         taxableAmount: newJson.taxableAmount || 0,
         gstTxnType: newJson.gstTxnType || 0,
         gstSubTxnType: newJson.gstSubTxnType || 0,
         assesseType: newJson.assesseType || 0,
         distance: newJson.distance || 0,
      };

      // Add type-specific date fields
      if (['purchases', 'purchasereturn', 'issue', 'receive'].includes(type)) {
         baseHeader.partyDate = safeDate(newJson.partyDate);
         baseHeader.partyNo = newJson.partyNo;
      }

      if (type === 'salesreturn') {
         baseHeader.originalBillDate = safeDate(newJson.originalBillDate);
         baseHeader.originalBillNo = newJson.originalBillNo;
      }

      // Merge with type-specific header
      const typeSpecificHeader = getTypeSpecificHeader(transactionType, newJson);
      const header = { ...baseHeader, ...typeSpecificHeader };

      // Remove voucherId for return types
      if (type === 'salesreturn' || type === 'purchasereturn') {
         delete header.voucherId;
      }

      // Process line items efficiently
      const lineItems = newJson.lineitems || [];
      const lineItem = lineItems.map((item, i) => {
         const baseLineItem = {
            typeOfSupply: item.typeOfSupply || null,
            itemId: item.itemId || null,
            lineNumber: i + 1,
            itemRate: item.itemRate || 0,
            amount: item.amount || 0,
            netAmount: item.netAmount || 0,
            ratePer: item.ratePer || 0,
            uomNoForRate: item.uomNoForRate || 0,
            qUnitId1: item.uomNoForRate || 160,
            qUnitId2: item.uomNoForRate || 160,
            qUnitId3: item.uomNoForRate || 160,
            qty1: item.qty1 || 0,
            qty2: item.qty2 || 0,
            qty3: item.qty3 || 0,
            addLess1Percent: item.addLess1Percent || 0,
            addLess1Amount: item.addLess1Amount || 0,
            addLess2Percent: item.addLess2Percent || 0,
            addLess2Amount: item.addLess2Amount || 0,
            addLess3Percent: item.addLess3Percent || 0,
            addLess3Amount: item.addLess3Amount || 0,
            taxableAmount: item.taxableAmount || 0,
            netGst: item.gstPercent || 0,
            gstPercent: item.gstPercent || 0,
            cgst: item.cgst || 0,
            sgst: item.sgst || 0,
            igst: item.igst || 0,
            issueLineId: null,
            hsnId: item.hsnId || null,
            sacId: item.sacId || null,
            subHSNId: null,
            takaDetails: [],
            warehouseId: item.warehouseId || null,
            isDeleted: false,
            gstWithRate: item.gstWithRate || 0,
         };

         const typeSpecificLineItem = getTypeSpecificLineItem(transactionType, item);
         return { ...baseLineItem, ...typeSpecificLineItem };
      });

      // Clean up salesreturn stock items
      if (type === 'salesreturn') {
         lineItem.forEach((item) => {
            if (item.stock && Array.isArray(item.stock)) {
               item.stock.forEach((stockItem) => {
                  delete stockItem.pinvLineDetailId;
               });
            }
         });
      }

      // Process vouchers if they exist
      if (Array.isArray(newJson.vouchers)) {
         newJson.vouchers.forEach((voucher) => {
            delete voucher.id;
            delete voucher.outAdjustmentId;
         });
      }

      const payments = newJson.vouchers;
      const billTerms = getBillTerms(transactionType, newJson);
      const billTermsKey = getBillTermsKey(transactionType);

      // Build result object
      const result = {
         header,
         lineItem,
         [billTermsKey]: billTerms,
      };

      // Add payments for purchase/sales
      if (['purchases', 'sales'].includes(type)) {
         result.payments = payments;
      }

      // Add exchange for sales if no exchange id
      if (type === 'sales' && !newJson.exchange?.id) {
         result.exchange = {
            exchange: [],
            payments: [],
         };
      }

      return result;
   }, []);

   // Optimized getAPIAction
   const getAPIAction = useCallback(
      async (tableName, logData) => {
         if (!tableName || !logData) return null;

         const table = tableName.toLowerCase();

         // Handle transaction types
         const transactionTypes = ['purchases', 'purchasereturn', 'sales', 'salesreturn', 'issue', 'receive'];

         if (transactionTypes.includes(table)) {
            const transformed = await transformTransactionData(logData, table);
            const action = TRANSACTION_ACTION_MAP[table];

            if (!action) {
               console.warn(`No action found for transaction type: ${table}`);
               return null;
            }

            return {
               action,
               payload: transformed,
            };
         }

         // Handle non-transaction types
         const action = API_ACTION_MAP[table];
         if (!action) {
            console.warn(`No API action found for table: ${table}`);
            return null;
         }

         // Process based on table type
         switch (table) {
            case 'account':
               const accountPayload = ACCOUNT_FORM_FIELDS.reduce((acc, field) => {
                  if (field in logData) {
                     acc[field] = logData[field];
                  }
                  return acc;
               }, {});

               const addressJson = logData?.accountaddress?.map((a) => ({
                  name: a?.name,
                  address1: a?.address1,
                  address2: a?.address2,
                  address3: a?.address3,
                  gst: a?.gst,
                  mobile: a?.mobile,
                  email: a?.email,
                  pincode: a?.pincode,
                  areaId: a?.areaId,
                  stateId: a?.stateId,
                  coId: a?.coId,
                  cityId: a?.cityId,
               }));

               return {
                  action,
                  payload: {
                     ...accountPayload,
                     addressJson: addressJson || [],
                  },
               };

            case 'item':
               const itemPayload = ITEM_FORM_FIELDS.reduce((acc, field) => {
                  if (field in logData) {
                     acc[field] = logData[field];
                  }
                  return acc;
               }, {});
               return { action, payload: itemPayload };

            case 'daybook':
               const daybookPayload = DAYBOOK_FORM_FIELDS.reduce((acc, field) => {
                  if (field in logData) {
                     acc[field] = logData[field];
                  }
                  return acc;
               }, {});
               return { action, payload: daybookPayload };

            case 'billterm':
               const billtermPayload = BILLTERM_FORM_FIELDS.reduce((acc, field) => {
                  if (field in logData) {
                     acc[field] = logData[field];
                  }
                  return acc;
               }, {});
               return { action, payload: billtermPayload };

            case 'bankbranch':
               const bankPayload = BANK_BRANCH_FORM_FIELDS.reduce((acc, field) => {
                  if (field in logData) {
                     acc[field] = logData[field];
                  }
                  return acc;
               }, {});
               return { action, payload: bankPayload };

            case 'transport':
               const transportPayload = TRANSPORT_FORM_FIELDS.reduce((acc, field) => {
                  if (field in logData) {
                     acc[field] = logData[field];
                  }
                  return acc;
               }, {});
               return { action, payload: transportPayload };

            case 'driver':
               const driverPayload = DRIVER_FORM_FIELDS.reduce((acc, field) => {
                  if (field in logData) {
                     acc[field] = logData[field];
                  }
                  return acc;
               }, {});
               return { action, payload: driverPayload };

            case 'vehicle':
               const vehiclePayload = VEHICLE_FORM_FIELDS.reduce((acc, field) => {
                  if (field in logData) {
                     acc[field] = logData[field];
                  }
                  return acc;
               }, {});
               return { action, payload: vehiclePayload };

            case 'color':
               const colorPayload = COLOR_FORM_FIELDS.reduce((acc, field) => {
                  if (field in logData) {
                     acc[field] = logData[field];
                  }
                  return acc;
               }, {});
               return { action, payload: colorPayload };

            case 'style':
               const stylePayload = STYLE_FORM_FIELDS.reduce((acc, field) => {
                  if (field in logData) {
                     acc[field] = logData[field];
                  }
                  return acc;
               }, {});
               return { action, payload: stylePayload };

            case 'size':
               const sizePayload = SIZE_FORM_FIELDS.reduce((acc, field) => {
                  if (field in logData) {
                     acc[field] = logData[field];
                  }
                  return acc;
               }, {});
               return { action, payload: sizePayload };

            case 'area':
               const areaPayload = AREA_FORM_FIELDS.reduce((acc, field) => {
                  if (field in logData) {
                     acc[field] = logData[field];
                  }
                  return acc;
               }, {});
               return { action, payload: areaPayload };

            case 'brand':
               const brandPayload = BRAND_FORM_FIELDS.reduce((acc, field) => {
                  if (field in logData) {
                     acc[field] = logData[field];
                  }
                  return acc;
               }, {});
               return { action, payload: brandPayload };

            case 'hsnmain':
               return { action, payload: logData };

            default:
               // For other tables, pass through with cleanup
               const payload = { ...logData };

               // Clean up specific fields based on table
               const cleanupMap = {
                  item: ['hsn', 'parentName', 'uqc1Data', 'uqc2Data', 'uqc3Data', 'vendorName'],
                  transport: ['modeName'],
                  driver: ['modeName'],
                  area: ['city', 'coid', 'stid', 'state', 'country'],
               };

               const fieldsToDelete = cleanupMap[table] || [];
               fieldsToDelete.forEach((field) => {
                  delete payload[field];
               });

               // Always remove system fields
               const systemFields = [
                  'id',
                  '_id',
                  'accountName',
                  'daybookName',
                  'bankBranchName',
                  'bankName',
                  'brokerName',
                  'supplierName',
                  'incomeId',
                  'createdAt',
                  'createdBy',
                  'createdU',
                  'createdu',
                  'createdby',
                  'updatedAt',
                  'updatedBy',
                  'updatedU',
                  'updatedby',
                  'updatedu',
                  'status',
                  'buffer',
               ];

               systemFields.forEach((field) => {
                  delete payload[field];
               });

               return { action, payload };
         }
      },
      [transformTransactionData],
   );

   // Optimized compareObjects function
   const compareObjects = useCallback((oldObj, newObj, path = '') => {
      const differences = [];

      // Early exit for identical references
      if (oldObj === newObj) return differences;

      // Handle null/undefined cases
      const isOldNull = oldObj === null || oldObj === undefined;
      const isNewNull = newObj === null || newObj === undefined;

      if (isOldNull && isNewNull) return differences;

      if (isOldNull) {
         // ADD operation
         Object.keys(newObj).forEach((key) => {
            differences.push({
               field: path ? `${path}.${key}` : key,
               oldValue: 'N/A',
               newValue: newObj[key],
               type: 'added',
            });
         });
         return differences;
      }

      if (isNewNull) {
         // DELETE operation
         Object.keys(oldObj).forEach((key) => {
            differences.push({
               field: path ? `${path}.${key}` : key,
               oldValue: oldObj[key],
               newValue: 'N/A',
               type: 'removed',
            });
         });
         return differences;
      }

      // Fast path for simple objects
      const allKeys = Object.keys({ ...oldObj, ...newObj });

      for (const key of allKeys) {
         const currentPath = path ? `${path}.${key}` : key;
         const oldValue = oldObj[key];
         const newValue = newObj[key];

         // Skip if values are equal (including null/undefined)
         if (oldValue === newValue) continue;

         // Handle arrays
         if (Array.isArray(oldValue) && Array.isArray(newValue)) {
            const maxLength = Math.max(oldValue.length, newValue.length);
            for (let i = 0; i < maxLength; i++) {
               if (oldValue[i] !== newValue[i]) {
                  differences.push({
                     field: `${currentPath}[${i}]`,
                     oldValue: oldValue[i] ?? 'N/A',
                     newValue: newValue[i] ?? 'N/A',
                     type: 'modified',
                  });
               }
            }
         }
         // Handle objects (skip deep comparison for performance)
         else if (typeof oldValue === 'object' && typeof newValue === 'object') {
            // For performance, just compare at first level
            if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
               differences.push({
                  field: currentPath,
                  oldValue: oldValue,
                  newValue: newValue,
                  type: 'modified',
               });
            }
         }
         // Handle primitives
         else {
            differences.push({
               field: currentPath,
               oldValue: oldValue ?? 'N/A',
               newValue: newValue ?? 'N/A',
               type: oldValue === undefined ? 'added' : newValue === undefined ? 'removed' : 'modified',
            });
         }
      }

      return differences;
   }, []);

   // Optimized formatFieldName
   const formatFieldName = useCallback((fieldName) => {
      const cached = FIELD_NAME_MAPPINGS[fieldName];
      if (cached) return cached;

      // Handle array patterns
      const lineItemMatch = fieldName.match(/^lineitems?\[(\d+)\]\.(.+)$/i);
      if (lineItemMatch) {
         const itemNumber = parseInt(lineItemMatch[1]) + 1;
         const subField = lineItemMatch[2];
         const mapped = FIELD_NAME_MAPPINGS[subField] || subField;
         return `Line Item ${itemNumber} - ${mapped}`;
      }

      // Handle vouchers
      const voucherMatch = fieldName.match(/^vouchers?\[(\d+)\]\.(.+)$/i);
      if (voucherMatch) {
         const voucherNumber = parseInt(voucherMatch[1]) + 1;
         const voucherField = voucherMatch[2];
         const mapped = FIELD_NAME_MAPPINGS[voucherField] || voucherField;
         return `Payment ${voucherNumber} - ${mapped}`;
      }

      // Return original if no match
      return fieldName;
   }, []);

   // Optimized differences calculation
   const differences = useMemo(() => {
      if (!logData || (!logData.oldJson && !logData.newJson)) {
         return EMPTY_ARRAY;
      }

      const removeBufferData = (json) => {
         if (!json) return null;
         const { buffer, ...rest } = json;
         return rest;
      };

      let rawDifferences = [];

      // Use a timeout to prevent blocking
      const startTime = performance.now();
      const MAX_PROCESSING_TIME = 50; // ms

      try {
         switch (logData.entryType) {
            case 'ADD':
               rawDifferences = compareObjects(null, removeBufferData(logData.newJson));
               break;
            case 'DELETE':
               rawDifferences = compareObjects(removeBufferData(logData.newJson), null);
               break;
            case 'UPDATE':
            default:
               rawDifferences = compareObjects(removeBufferData(logData.oldJson), removeBufferData(logData.newJson));
               break;
         }

         // Check if we're taking too long
         if (performance.now() - startTime > MAX_PROCESSING_TIME) {
            console.warn('Differences calculation taking too long:', performance.now() - startTime);
         }
      } catch (error) {
         console.error('Error calculating differences:', error);
         return EMPTY_ARRAY;
      }

      // Fast filtering
      return rawDifferences
         .filter((diff) => {
            // Skip N/A to null comparisons
            if (diff.oldValue === 'N/A' && diff.newValue === null) return false;

            const fieldName = diff.field.toLowerCase();

            // Fast ID field check
            if (ID_FIELDS_TO_EXCLUDE.has(fieldName) || fieldName.endsWith('id') || fieldName.endsWith('_id')) {
               return false;
            }

            return true;
         })
         .map((diff) => ({
            ...diff,
            field: formatFieldName(diff.field),
            originalField: diff.field,
         }))
         .slice(0, 500); // Limit to 500 differences for performance
   }, [logData, compareObjects, formatFieldName]);

   // Optimized fetch functions
   const fetchDetailedLogData = useCallback(async (codeToUse = accessCode) => {
      if (!initialLogDataRef.current?.id) return;
      console.log('codeToUse:', codeToUse);

      try {
         setIsLoadingLogData(true);
         const detailedLogData = await dispatchRef.current(getSingleLog(initialLogDataRef.current.id, codeToUse));
         if (detailedLogData) {
            setLogData(detailedLogData);
            onOpenPayload();
            setTempAccessCode('');
         }
      } catch (error) {
         console.error('Failed to fetch detailed log data:', error);
         toastRef.current({
            title: 'Error',
            description: 'Failed to load detailed log data. Please check your access code and try again.',
            status: 'error',
            duration: 3000,
            isClosable: true,
         });
      } finally {
         setIsLoadingLogData(false);
      }
   }, []);

   const handleSetAccessCode = useCallback(() => {
      setAccessCode(tempAccessCode);
      onAccessCodeModalClose();
      fetchDetailedLogData(tempAccessCode);
   }, [tempAccessCode, onAccessCodeModalClose, fetchDetailedLogData]);

   const handleAccessCodeModalOpen = useCallback(() => {
      setTempAccessCode(accessCode);
      onAccessCodeModalOpen();
   }, [accessCode, onAccessCodeModalOpen]);

   const handleRestoreData = async () => {
      if (!logData || logData.entryType !== 'DELETE' || !logData.newJson) {
         toast({
            title: 'Cannot Restore',
            description: 'No deleted data available to restore.',
            status: 'error',
            duration: 3000,
            isClosable: true,
         });
         return;
      }

      const apiInfo = await getAPIAction(logData.tableName, logData.newJson);

      if (!apiInfo || !apiInfo.action) {
         toast({
            title: 'API Not Found',
            description: `No API action found for table: ${logData.tableName}`,
            status: 'error',
            duration: 3000,
            isClosable: true,
         });
         return;
      }

      setIsRestoring(true);

      try {
         let payload;

         // Check if this is a transaction type that needs transformation
         const transactionTypes = ['purchases', 'purchasereturn', 'sales', 'salesreturn', 'issue', 'receive'];

         if (transactionTypes.includes(logData.tableName?.toLowerCase())) {
            // Use the transformed payload
            payload = await apiInfo.payload;
         } else if (logData.tableName?.toLowerCase() === 'account') {
            payload = await apiInfo.payload;
            delete payload.id;
            delete payload.updatedAt;
            delete payload.companyIds;
         } else if (logData.tableName?.toLowerCase() === 'item') {
            payload = await apiInfo.payload;
            delete payload.id;
            delete payload.updatedAt;
            delete payload.companyIds;
         } else if (logData.tableName?.toLowerCase() === 'daybook') {
            payload = await apiInfo.payload;
            delete payload.isAbs;
            delete payload.sendEmail;
            delete payload.emailSettings;
            delete payload.allowZeroLineItems;
            delete payload.promptbeforecallosadj;
            delete payload.createdAt;
            delete payload.updatedAt;
            delete payload.createdBy;
            delete payload.updatedBy;
         } else if (logData.tableName?.toLowerCase() === 'transport') {
            payload = await apiInfo.payload;
            delete payload.id;
            delete payload.updatedAt;
            delete payload.companyIds;
            delete payload.createdAt;
            delete payload.updatedAt;
            delete payload.createdBy;
            delete payload.updatedBy;
            delete payload.companyIds;
         } else if (logData.tableName?.toLowerCase() === 'driver') {
            payload = await apiInfo.payload;
            delete payload.id;
            delete payload.updatedAt;
            delete payload.companyIds;
            delete payload.createdAt;
            delete payload.updatedAt;
            delete payload.createdBy;
            delete payload.updatedBy;
         } else if (logData.tableName?.toLowerCase() === 'vehicle') {
            payload = await apiInfo.payload;
            delete payload.id;
            delete payload.updatedAt;
            delete payload.companyIds;
            delete payload.createdAt;
            delete payload.updatedAt;
            delete payload.createdBy;
            delete payload.updatedBy;
         } else if (logData.tableName?.toLowerCase() === 'color') {
            payload = await apiInfo.payload;
            delete payload.id;
            delete payload.updatedAt;
            delete payload.companyIds;
            delete payload.createdAt;
            delete payload.updatedAt;
            delete payload.createdBy;
            delete payload.updatedBy;
         } else if (logData.tableName?.toLowerCase() === 'style') {
            payload = await apiInfo.payload;
            delete payload.id;
            delete payload.updatedAt;
            delete payload.companyIds;
            delete payload.createdAt;
            delete payload.updatedAt;
            delete payload.createdBy;
            delete payload.updatedBy;
         } else if (logData.tableName?.toLowerCase() === 'size') {
            payload = await apiInfo.payload;
            delete payload.id;
            delete payload.updatedAt;
            delete payload.companyIds;
            delete payload.createdAt;
            delete payload.updatedAt;
            delete payload.createdBy;
            delete payload.updatedBy;
         } else if (logData.tableName?.toLowerCase() === 'area') {
            payload = await apiInfo.payload;
            delete payload.id;
            delete payload.updatedAt;
            delete payload.companyIds;
            delete payload.createdAt;
            delete payload.updatedAt;
            delete payload.createdBy;
            delete payload.updatedBy;
         } else if (logData.tableName?.toLowerCase() === 'brand') {
            payload = await apiInfo.payload;
            delete payload.id;
            delete payload.updatedAt;
            delete payload.companyIds;
            delete payload.createdAt;
            delete payload.updatedAt;
            delete payload.createdBy;
            delete payload.updatedBy;
         } else if (logData.tableName?.toLowerCase() === 'billterm') {
            payload = await apiInfo.payload;
            delete payload.id;
            delete payload.updatedAt;
            delete payload.companyIds;
            delete payload.createdAt;
            delete payload.updatedAt;
            delete payload.createdBy;
            delete payload.updatedBy;
         } else {
            // For non-transaction types (like voucher), use the original logic
            payload = { ...logData.newJson };
            if (logData.tableName?.toLowerCase() === 'item') {
               delete payload.hsn;
               delete payload.parentName;
               delete payload.uqc1Data;
               delete payload.uqc2Data;
               delete payload.uqc3Data;
               delete payload.vendorName;
            }
            if (logData.tableName?.toLowerCase() === 'transport') {
               delete payload.modeName;
            }
            if (logData.tableName?.toLowerCase() === 'driver') {
               delete payload.modeName;
            }

            if (logData.tableName?.toLowerCase() === 'area') {
               delete payload.city;
               delete payload.coid;
               delete payload.stid;
               delete payload.state;
               delete payload.country;
            }

            // Remove system fields that shouldn't be restored
            delete payload.id; // Let the system generate new ID
            delete payload.accountName;
            delete payload.daybookName;
            delete payload.bankBranchName;
            delete payload.bankName;
            delete payload.brokerName;
            delete payload.supplierName;
            delete payload.bankBranchName;
            delete payload.incomeId;
            delete payload.createdAt;
            delete payload.createdBy;
            delete payload.createdU;
            delete payload.createdu;
            delete payload.createdby;
            delete payload.updatedAt;
            delete payload.updatedBy;
            delete payload.updatedU;
            delete payload.updatedby;
            delete payload.updatedu;
            delete payload.status;
            delete payload.buffer;
         }
         if (transactionTypes.includes(logData.tableName?.toLowerCase())) {
            const res = await checkTransactionDocNo(
               logData.tableName?.toLowerCase(),
               payload?.header?.daybookId,
               payload?.header?.docno,
               payload?.header?.companyId,
            );

            if (res && res.code === 1 && res?.data?.isError) {
               Swal.fire({
                  title: res?.data?.message,
                  showCancelButton: true,
                  confirmButtonText: 'Yes',
                  cancelButtonText: 'No',
                  cancelButtonColor: '#d33',
                  confirmButtonColor: '#3085d6',
               }).then(async (swalResult) => {
                  if (swalResult.isConfirmed) {
                     const newPayload = { ...payload };
                     newPayload.header.docno = res.data?.newDocNo?.toString?.();

                     const dispatchResult = await dispatch(apiInfo.action(newPayload));

                     if (dispatchResult && dispatchResult?.code === 1) {
                        await dispatch(updateLogs({ id: logData?.id, isRestored: true }));
                        return;
                     }
                     if (dispatchResult && dispatchResult?.code === 2) {
                        console.log('result', dispatchResult?.message);
                        return;
                     }
                     return;
                  }
                  // return;
               });
            } else {
               const dispatchResult = await dispatch(apiInfo.action(payload));

               if (dispatchResult && dispatchResult?.code === 1) {
                  await dispatch(updateLogs({ id: logData?.id, isRestored: true }));
                  return;
               }
               if (dispatchResult && dispatchResult?.code === 2) {
                  return;
               }
               return;
            }
         } else if (logData.tableName?.toLowerCase() === 'voucher') {
            const financialYear = financialYears.find((a) => a.financial_year === user?.financialYear);

            const res = await dispatch(
               checkLastDocNumberVoucher({
                  companyId: payload.companyId,
                  daybookId: payload.daybookId,
                  txnType: payload.txnType,
                  type: payload.daybookType,
                  docNumber: payload.docNumber,
                  fromDate: moment(financialYear?.fy_start_date, 'DD/MM/YYYY').format('YYYY-MM-DD'),
                  toDate: moment(financialYear?.fy_end_date, 'DD/MM/YYYY').format('YYYY-MM-DD'),
               }),
            );

            if (res && res.code === 1 && res?.data?.isError) {
               Swal.fire({
                  title: res?.data?.message,
                  showCancelButton: true,
                  confirmButtonText: 'Yes',
                  cancelButtonText: 'No',
                  cancelButtonColor: '#d33',
                  confirmButtonColor: '#3085d6',
               }).then(async (result) => {
                  if (result.isConfirmed) {
                     const newPayload = { ...payload };
                     newPayload.outAdjustments = newPayload?.outAdjustments?.map((oa) => {
                        return {
                           drDocHeaderId: oa.drDocHeaderId,
                           drDaybookId: oa.drDaybookId,
                           amount: oa.amount,
                           notes: oa.notes || '',
                        };
                     });
                     newPayload.docNumber = res.data?.newDocNo.toString();
                     delete newPayload.daybookType;
                     const result = await dispatch(apiInfo.action(newPayload));
                     if (result && !result.error) {
                        await dispatch(updateLogs({ id: logData?.id, isRestored: true }));
                     } else {
                        toast.error(result.error?.message || 'Restoration failed');
                     }
                  }
               });
            }

            const newPayload = { ...payload };
            newPayload.outAdjustments = newPayload?.outAdjustments?.map((oa) => {
               return {
                  drDocHeaderId: oa.drDocHeaderId,
                  drDaybookId: oa.drDaybookId,
                  amount: oa.amount,
                  notes: oa.notes || '',
               };
            });
            delete newPayload.daybookType;
            payload = newPayload;
         } else {
            // Dispatch the appropriate API action
            const result = await dispatch(apiInfo.action(payload));

            // Handle successful restoration
            if (result && !result.error) {
               await dispatch(updateLogs({ id: logData?.id, isRestored: true }));
            } else {
               toast.error(result.error?.message || 'Restoration failed');
            }
         }
      } catch (error) {
         console.error('Error restoring data:', error);
      } finally {
         setIsRestoring(false);
      }
   };

   // Optimized useEffect for fetching log data
   useEffect(() => {
      let mounted = true;

      const fetchLogData = async () => {
         if (isOpenRef.current && initialLogDataRef.current?.id && accessCodeRef.current) {
            try {
               setIsLoadingLogData(true);
               const detailedLogData = await dispatchRef.current(
                  getSingleLog(initialLogDataRef.current.id, accessCodeRef.current),
               );

               if (mounted && detailedLogData) {
                  setLogData(detailedLogData);
                  onOpenPayload();
                  setTempAccessCode('');
               }
            } catch (error) {
               if (mounted) {
                  console.error('Failed to fetch detailed log data:', error);
                  toastRef.current({
                     title: 'Error',
                     description: 'Failed to load detailed log data. Please try again.',
                     status: 'error',
                     duration: 3000,
                     isClosable: true,
                  });
               }
            } finally {
               if (mounted) {
                  setIsLoadingLogData(false);
               }
            }
         }
      };

      fetchLogData();

      return () => {
         mounted = false;
      };
   }, [isOpen, initialLogData?.id, accessCode, onOpenPayload]);

   // Reset logData when modal closes
   useEffect(() => {
      if (!isOpen) {
         setLogData(initialLogData);
      }
   }, [isOpen, initialLogData]);

   const openLocationInMaps = useCallback(() => {
      const deviceData = logData?.deviceData;
      const lat = deviceData?.latitude || deviceData?.lat;
      const lng = deviceData?.longitude || deviceData?.long;

      if (lat && lng) {
         window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank');
      } else {
         alert('Location data not available');
      }
   }, [logData?.deviceData]);

   const getChangeTypeColor = useCallback((type) => {
      switch (type) {
         case 'added':
            return 'green';
         case 'removed':
            return 'red';
         case 'modified':
            return 'blue';
         default:
            return 'gray';
      }
   }, []);

   const getEntryTypeMessage = useCallback(() => {
      switch (logData?.entryType) {
         case 'ADD':
            return 'All fields shown below were added in this creation operation.';
         case 'DELETE':
            return 'All fields shown below were removed in this deletion operation.';
         case 'UPDATE':
            return `changes detected in this update operation.`;
         default:
            return `changes detected in this ${logData?.entryType?.toLowerCase()} operation.`;
      }
   }, [logData?.entryType]);

   const handleClose = useCallback(() => {
      onClose();
   }, [onClose]);

   if (!logData) {
      return null;
   }

   return (
      <>
         {/* Main Modal */}
         <Modal isOpen={isOpen} onClose={handleClose} size="6xl" scrollBehavior="inside" blockScrollOnMount={false}>
            <ModalOverlay />
            <ModalContent maxH="90vh">
               <ModalHeader>
                  <VStack align="start" spacing={1}>
                     <HStack justify="space-between" width="100%">
                        <Text fontSize="xl" fontWeight="semibold">
                           Change History - {logData.tableName}
                        </Text>

                        {logData.entryType === 'DELETE' && logData.newJson && (
                           <Button
                              leftIcon={isRestoring ? <Spinner size="sm" /> : <RepeatIcon />}
                              colorScheme="green"
                              variant="solid"
                              size="sm"
                              mr="7"
                              onClick={handleRestoreData}
                              isLoading={isRestoring}
                              loadingText="Restoring..."
                              isDisabled={isRestoring}>
                              Restore Data
                           </Button>
                        )}
                     </HStack>

                     <HStack spacing={4}>
                        <Text fontSize="sm" color="gray.600">
                           Entry Type:{' '}
                           <Text
                              as="span"
                              fontWeight="medium"
                              color={
                                 logData.entryType === 'ADD'
                                    ? 'green.600'
                                    : logData.entryType === 'DELETE'
                                       ? 'red.600'
                                       : logData.entryType === 'UPDATE'
                                          ? 'blue.600'
                                          : 'gray.600'
                              }>
                              {logData.entryType}
                           </Text>
                        </Text>
                        <Text fontSize="sm" color="gray.600">
                           Created At: {new Date(logData.createdAt).toLocaleString()}
                        </Text>
                        <Text fontSize="sm" color="gray.600">
                           By: {logData.createdby || `User ${logData.createdBy}`}
                        </Text>
                        <Button
                           size="sm"
                           leftIcon={<FaLock />}
                           colorScheme="blue"
                           variant="outline"
                           onClick={handleAccessCodeModalOpen}>
                           Developers Section
                        </Button>
                        {logData.deviceData && (
                           <Button
                              size="sm"
                              leftIcon={<InfoIcon />}
                              colorScheme="blue"
                              variant="outline"
                              onClick={onDeviceModalOpen}>
                              Device Info
                           </Button>
                        )}
                     </HStack>
                  </VStack>
               </ModalHeader>
               <ModalCloseButton />

               <ModalBody pb={6}>
                  {logData.entryType === 'DELETE' && (
                     <Alert status="warning" variant="left-accent" mb={4}>
                        <AlertIcon />
                        <Box>
                           <Text fontSize="sm" fontWeight="semibold">
                              Deleted Data Recovery Available
                           </Text>
                           <Text fontSize="xs" color="gray.600">
                              This data was deleted and can be restored using the "Restore Data" button above.
                           </Text>
                        </Box>
                     </Alert>
                  )}

                  {differences.length === 0 ? (
                     <Box textAlign="center" py={8}>
                        <Text color="gray.500">No changes detected between old and new data.</Text>
                     </Box>
                  ) : (
                     <VStack spacing={6} align="stretch">
                        <Alert
                           status={
                              logData.entryType === 'ADD'
                                 ? 'success'
                                 : logData.entryType === 'DELETE'
                                    ? 'error'
                                    : 'info'
                           }
                           variant="left-accent">
                           <AlertIcon />
                           <Text fontSize="sm">
                              <Text as="span" fontWeight="bold">
                                 {differences.length}
                              </Text>{' '}
                              {getEntryTypeMessage()}
                           </Text>
                        </Alert>

                        <Box borderWidth={1} borderRadius="md" overflow="hidden">
                           <Box overflowX="auto">
                              <Table variant="simple" size="sm">
                                 <Thead bg="gray.100">
                                    <Tr>
                                       <Th width="25%" border="1px solid" borderColor="black">
                                          Field
                                       </Th>
                                       <Th width="30%" border="1px solid" borderColor="black">
                                          Old Data
                                       </Th>
                                       <Th width="30%" border="1px solid" borderColor="black">
                                          New Data
                                       </Th>
                                       <Th width="15%" border="1px solid" borderColor="black">
                                          Change Type
                                       </Th>
                                    </Tr>
                                 </Thead>
                                 <Tbody>
                                    {differences.map((diff, index) => (
                                       <DifferenceRow key={`${diff.field}-${index}`} diff={diff} />
                                    ))}
                                 </Tbody>
                              </Table>
                           </Box>
                        </Box>
                     </VStack>
                  )}
               </ModalBody>
            </ModalContent>
         </Modal>

         {/* Device Info Modal */}
         <Modal isOpen={isDeviceModalOpen} onClose={onDeviceModalClose} size="4xl">
            <ModalOverlay />
            <ModalContent maxH="90vh" display="flex" flexDirection="column">
               <ModalHeader flexShrink={0}>
                  <HStack justify="space-between" align="center">
                     <Text fontSize="xl" fontWeight="semibold">
                        Device Information
                     </Text>
                     {(logData?.deviceData?.latitude || logData?.deviceData?.publicIp) && (
                        <Button
                           size="sm"
                           leftIcon={<ExternalLinkIcon />}
                           colorScheme="green"
                           variant="outline"
                           mr={5}
                           onClick={openLocationInMaps}>
                           View Location
                        </Button>
                     )}
                  </HStack>
               </ModalHeader>
               <ModalCloseButton />

               <ModalBody pb={6} flex="1" overflowY="auto" minH={0} /* Important for flex child scrolling */>
                  {logData?.deviceData ? (
                     <Box>
                        <Alert status="info" variant="left-accent" mb={4}>
                           <AlertIcon />
                           <Text fontSize="sm">
                              Device and browser information captured at the time of this operation.
                           </Text>
                        </Alert>

                        <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                           {Object.entries(logData.deviceData).map(([key, value]) => (
                              <DeviceInfoItem key={key} value={value} />
                           ))}
                        </Grid>
                     </Box>
                  ) : (
                     <Box textAlign="center" py={8}>
                        <Text color="gray.500">No device information available.</Text>
                     </Box>
                  )}
               </ModalBody>
            </ModalContent>
         </Modal>

         {/* Access Code Modal */}
         <Modal isOpen={isAccessCodeModalOpen} onClose={onAccessCodeModalClose} size="md">
            <ModalOverlay />
            <ModalContent>
               <ModalHeader>
                  <Text fontSize="xl" fontWeight="semibold">
                     Developers Section
                  </Text>
               </ModalHeader>
               <ModalCloseButton />

               <ModalBody pb={6}>
                  <FormControl>
                     <FormLabel fontSize="sm" fontWeight="medium">
                        Developers Section Code
                     </FormLabel>
                     <Input
                        type="password"
                        placeholder="Enter access code..."
                        value={tempAccessCode}
                        onChange={(e) => setTempAccessCode(e.target.value)}
                        onKeyPress={(e) => {
                           if (e.key === 'Enter') {
                              handleSetAccessCode();
                           }
                        }}
                     />
                  </FormControl>
               </ModalBody>

               <ModalFooter>
                  <Button variant="outline" mr={3} onClick={onAccessCodeModalClose}>
                     Cancel
                  </Button>
                  <Button
                     colorScheme="blue"
                     onClick={handleSetAccessCode}
                     isLoading={isLoadingLogData}
                     loadingText="Fetching..."
                     isDisabled={isLoadingLogData}>
                     Fetch Log Data
                  </Button>
               </ModalFooter>
            </ModalContent>
         </Modal>

         {/* Payload Modal */}
         <Modal isOpen={isOpenPayload} onClose={onClosePayload} size="4xl" blockScrollOnMount={false}>
            <ModalOverlay />
            <ModalContent>
               <ModalHeader>
                  <Text fontSize="xl" fontWeight="semibold">
                     Payload
                  </Text>
               </ModalHeader>
               <ModalCloseButton />

               <ModalBody pb={6}>
                  <Box borderWidth={1} borderRadius="md" overflow="hidden">
                     <Box overflowX="auto">
                        <Table variant="simple" size="sm">
                           <Thead bg="gray.100">
                              <Tr>
                                 <Th width="10%" border="1px solid" borderColor="black">
                                    Data
                                 </Th>
                              </Tr>
                           </Thead>
                           <Tbody>
                              <Tr>
                                 <Td border="1px solid" borderColor="black">
                                    <JsonViewer payload={logData.payload} />
                                 </Td>
                              </Tr>
                           </Tbody>
                        </Table>
                     </Box>
                  </Box>
               </ModalBody>

               <ModalFooter>
                  <Button variant="outline" mr={3} onClick={onClosePayload}>
                     Cancel
                  </Button>
               </ModalFooter>
            </ModalContent>
         </Modal>
      </>
   );
};

export default JsonDifferenceModal;
